import { RootState } from '../../store/store';
import { UploadProcessStep } from '../../store/storeModels';

/**
 * Single-source-of-truth enum for the upload UX. Replaces the boolean-soup
 * (`isUploadError`, `isResumingUpload`, `isUploadInprogress`, `currentStep`,
 * `uploadingProgress === 100`) that components used to combine ad-hoc.
 *
 * Some phases require state introduced by later phases of the resilience
 * work (e.g. `PausedWifi` and `NeedsRepick` come from a local `pausedAt`
 * flag and the persisted resume record respectively) — those are still
 * declared here so the copy map and the UI banner can fan out on a single
 * type, even if today's selector never returns them.
 */
export enum UploadPhase {
  Idle = 'idle',
  CheckingConnection = 'checking_connection',
  Uploading = 'uploading',
  UploadingSlow = 'uploading_slow',
  Dipped = 'dipped',
  PausedWifi = 'paused_wifi',
  Resuming = 'resuming',
  Finalizing = 'finalizing',
  NeedsRepick = 'needs_repick',
  Complete = 'complete',
  ErrorFatal = 'error_fatal',
}

/**
 * Derive the high-level phase from raw Redux state. Pure / deterministic so
 * components can memoize on it.
 *
 * Today, `isUploadError` is set both for transient drops (auto-retry loop
 * recovers in <5 s) and for unrecoverable failures — we don't have a clean
 * signal to tell them apart, so we surface them as `Dipped`. A future
 * `error_fatal` slice field (Phase 6) can flip these to `ErrorFatal`.
 */
export const selectUploadPhase = (state: RootState): UploadPhase => {
  const {
    currentStep,
    isUploadInprogress,
    uploadingProgress,
    isUploadError,
    isUploadFatal,
    isResumingUpload,
    isSlowConnection,
    isPausedForWifi,
  } = state.upload;
  const questionsAnswered = (state.ui.questions.items?.length ?? 0) > 1;

  // Paused-for-wifi is sticky — user-initiated, only the resume action clears.
  if (isPausedForWifi) return UploadPhase.PausedWifi;

  // Fatal must precede transient: the slice keeps both flags coherent (fatal
  // also sets the transient one), and we want the user-facing copy to reflect
  // "this won't recover on its own" rather than "we'll auto-retry".
  if (isUploadFatal) return UploadPhase.ErrorFatal;

  if (isUploadError) {
    // Transient — the auto-retry loop in UploadFlowCarousel recovers most of
    // these within 5 s; the copy reflects that promise.
    return UploadPhase.Dipped;
  }

  if (isResumingUpload) return UploadPhase.Resuming;

  if (currentStep === UploadProcessStep.UploadVideo) return UploadPhase.Idle;

  if (currentStep === UploadProcessStep.Uploaded) {
    return questionsAnswered ? UploadPhase.Complete : UploadPhase.Finalizing;
  }

  if (currentStep === UploadProcessStep.VideoUploading) {
    if (uploadingProgress >= 100 && !isUploadInprogress) {
      return UploadPhase.Finalizing;
    }
    return isSlowConnection ? UploadPhase.UploadingSlow : UploadPhase.Uploading;
  }

  return UploadPhase.Idle;
};
